import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import {
  fetchCompetitionQuestions,
  adminCreateQuestion,
  adminUpdateQuestion,
  adminDeleteQuestion,
  adminResolveQuestion,
  adminSubmitQuestionAnswer,
  competitionGroupsPath
} from '../services/competitionApi';
import { getCompetitionDisplayName } from '../utils/competitionDisplayName';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { getQuestionTitle, getOptionLabel, formatAnswerLabel } from '../utils/questionDisplayName';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, CheckCircle, HelpCircle, Minus, UserPlus, X } from 'lucide-react';

const defaultOptions = () => [
  { optionId: 'opt-1', labelHe: '', labelEn: '' },
  { optionId: 'opt-2', labelHe: '', labelEn: '' }
];

function nextOptionId(options) {
  const nums = options.map((o) => {
    const m = String(o.optionId || '').match(/^opt-(\d+)$/);
    return m ? parseInt(m[1], 10) : 0;
  });
  return `opt-${Math.max(0, ...nums) + 1}`;
}

const emptyForm = () => ({
  titleHe: '',
  titleEn: '',
  type: 'team',
  points: 3,
  sortOrder: 0,
  options: defaultOptions()
});

const AdminQuestions = () => {
  const { t, language, isRTL } = useLanguage();
  const { competitionSlug, currentCompetition } = useCompetition();
  const [questions, setQuestions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [resolveQ, setResolveQ] = useState(null);
  const [resolveValue, setResolveValue] = useState({});
  const [answerForm, setAnswerForm] = useState({
    userId: '',
    questionId: '',
    teamGroupId: '',
    text: '',
    optionId: ''
  });
  const [savingAnswer, setSavingAnswer] = useState(false);

  const compName = getCompetitionDisplayName(currentCompetition, language);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [q, tr, usersRes] = await Promise.all([
        fetchCompetitionQuestions(competitionSlug),
        axios.get(competitionGroupsPath(competitionSlug)),
        axios.get('/users')
      ]);
      setQuestions(q);
      setTeams(Array.isArray(tr.data) ? tr.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (e) {
      toast.error(language === 'en' ? 'Failed to load questions' : 'נכשל בטעינת השאלות');
    } finally {
      setLoading(false);
    }
  }, [competitionSlug, language]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (q) => {
    setEditingId(q._id);
    setForm({
      titleHe: q.titleHe || '',
      titleEn: q.titleEn || '',
      type: q.type,
      points: q.points ?? 3,
      sortOrder: q.sortOrder ?? 0,
      options: q.options?.length ? q.options : defaultOptions()
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.type === 'options') {
      if (form.options.length < 2) {
        toast.error(
          language === 'en' ? 'Add at least 2 options' : 'הוסף לפחות 2 אפשרויות'
        );
        return;
      }
      const missingLabel = form.options.some(
        (o) => !o.labelHe.trim() || !o.labelEn.trim()
      );
      if (missingLabel) {
        toast.error(
          language === 'en'
            ? 'Each option needs Hebrew and English labels'
            : 'לכל אפשרות נדרשת תווית בעברית ובאנגלית'
        );
        return;
      }
    }
    const body = {
      titleHe: form.titleHe.trim(),
      titleEn: form.titleEn.trim(),
      type: form.type,
      points: Number(form.points) || 3,
      sortOrder: Number(form.sortOrder) || 0,
      options: form.type === 'options' ? form.options : []
    };
    try {
      if (editingId) {
        await adminUpdateQuestion(competitionSlug, editingId, body);
        toast.success(language === 'en' ? 'Updated' : 'עודכן');
      } else {
        await adminCreateQuestion(competitionSlug, body);
        toast.success(language === 'en' ? 'Created' : 'נוצר');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || (language === 'en' ? 'Failed' : 'נכשל'));
    }
  };

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        { optionId: nextOptionId(prev.options), labelHe: '', labelEn: '' }
      ]
    }));
  };

  const removeOption = (index) => {
    setForm((prev) => {
      if (prev.options.length <= 2) return prev;
      return { ...prev, options: prev.options.filter((_, i) => i !== index) };
    });
  };

  const handleDelete = async (q) => {
    if (!window.confirm(language === 'en' ? 'Delete this question?' : 'למחוק שאלה זו?')) return;
    try {
      await adminDeleteQuestion(competitionSlug, q._id);
      toast.success(language === 'en' ? 'Deleted' : 'נמחק');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || (language === 'en' ? 'Delete failed' : 'מחיקה נכשלה'));
    }
  };

  const openResolve = (q) => {
    setResolveQ(q);
    setResolveValue({ acceptedTexts: [], customText: '' });
  };

  const textAnswerChoices = (() => {
    if (resolveQ?.type !== 'text') return [];
    const grouped = new Map();
    for (const answer of resolveQ.answers || []) {
      const text = String(answer.answer?.text || '').trim();
      const key = text.toLowerCase();
      if (!key) continue;
      const existing = grouped.get(key);
      if (existing) {
        existing.count += 1;
        existing.users.push(answer.userId?.fullName || answer.userId?.username || '');
      } else {
        grouped.set(key, {
          text,
          count: 1,
          users: [answer.userId?.fullName || answer.userId?.username || '']
        });
      }
    }
    return Array.from(grouped.values());
  })();

  const toggleAcceptedText = (text) => {
    setResolveValue((current) => {
      const accepted = current.acceptedTexts || [];
      const selected = accepted.some((value) => value.toLowerCase() === text.toLowerCase());
      return {
        ...current,
        acceptedTexts: selected
          ? accepted.filter((value) => value.toLowerCase() !== text.toLowerCase())
          : [...accepted, text]
      };
    });
  };

  const addCustomAcceptedText = () => {
    const text = String(resolveValue.customText || '').trim();
    if (!text) return;
    setResolveValue((current) => {
      const accepted = current.acceptedTexts || [];
      const exists = accepted.some((value) => value.toLowerCase() === text.toLowerCase());
      return {
        ...current,
        acceptedTexts: exists ? accepted : [...accepted, text],
        customText: ''
      };
    });
  };

  const openAnswerModal = (questionId = '') => {
    setAnswerForm({
      userId: '',
      questionId: questionId || '',
      teamGroupId: '',
      text: '',
      optionId: ''
    });
    setSearchUserTerm('');
    setShowAnswerModal(true);
  };

  const selectedAnswerQuestion = questions.find((q) => q._id === answerForm.questionId);

  const filteredUsers = users.filter(
    (user) =>
      user.username?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      (user.fullName && user.fullName.toLowerCase().includes(searchUserTerm.toLowerCase()))
  );

  const editableQuestions = questions.filter((q) => q.status !== 'resolved');

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    if (!answerForm.userId || !answerForm.questionId) {
      toast.error(language === 'en' ? 'Select user and question' : 'בחר משתמש ושאלה');
      return;
    }

    const q = questions.find((item) => item._id === answerForm.questionId);
    if (!q) return;

    let body = { userId: answerForm.userId };
    if (q.type === 'team') {
      if (!answerForm.teamGroupId) {
        toast.error(language === 'en' ? 'Select a team' : 'בחר נבחרת');
        return;
      }
      body = { ...body, teamGroupId: answerForm.teamGroupId };
    } else if (q.type === 'text') {
      if (!answerForm.text.trim()) {
        toast.error(language === 'en' ? 'Enter an answer' : 'הזן תשובה');
        return;
      }
      body = { ...body, text: answerForm.text.trim() };
    } else {
      if (!answerForm.optionId) {
        toast.error(language === 'en' ? 'Select an option' : 'בחר אפשרות');
        return;
      }
      body = { ...body, optionId: answerForm.optionId };
    }

    try {
      setSavingAnswer(true);
      await adminSubmitQuestionAnswer(competitionSlug, answerForm.questionId, body);
      toast.success(language === 'en' ? 'Prediction saved' : 'התחזית נשמרה');
      setShowAnswerModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || (language === 'en' ? 'Save failed' : 'שמירה נכשלה'));
    } finally {
      setSavingAnswer(false);
    }
  };

  const submitResolve = async () => {
    if (!resolveQ) return;
    try {
      let body = {};
      if (resolveQ.type === 'team') body = { teamGroupId: resolveValue.teamGroupId };
      else if (resolveQ.type === 'text') {
        if (!(resolveValue.acceptedTexts || []).length) {
          toast.error(
            language === 'en'
              ? 'Select at least one accepted answer'
              : 'בחר לפחות תשובה נכונה אחת'
          );
          return;
        }
        body = { texts: resolveValue.acceptedTexts };
      }
      else body = { optionId: resolveValue.optionId };
      await adminResolveQuestion(competitionSlug, resolveQ._id, body);
      toast.success(language === 'en' ? 'Resolved and points awarded' : 'נפתר ונקודות חושבו');
      setResolveQ(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || (language === 'en' ? 'Resolve failed' : 'פתרון נכשל'));
    }
  };

  return (
    <AdminLayout
      title={language === 'en' ? 'Prediction questions' : 'שאלות תחזית'}
      subtitle={compName}
      loading={loading}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" onClick={() => openAnswerModal()}>
            <UserPlus className="h-4 w-4" />
            {language === 'en' ? 'Add prediction' : 'הוסף תחזית'}
          </Button>
          <Button size="sm" variant="secondary" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {language === 'en' ? 'Add question' : 'הוסף שאלה'}
          </Button>
        </div>
      }
    >
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        {language === 'en'
          ? 'As admin you can add or update a user’s pre-tournament prediction even after questions are locked for users (until the question is resolved).'
          : 'כמנהל ניתן להוסיף או לעדכן תחזית למשתמש גם אחרי שהשאלות ננעלו למשתמשים (עד פתרון השאלה).'}
      </div>
      <div className="space-y-4">
        {questions.map((q) => (
          <Card key={q._id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex gap-2">
                <HelpCircle className="h-5 w-5 text-brand-600" />
                <div>
                  <h3 className="font-bold text-slate-900">{getQuestionTitle(q, language)}</h3>
                  <p className="text-xs text-slate-500">
                    {q.type} · {q.status} · {q.points} pts · {q.answers?.length || 0}{' '}
                    {language === 'en' ? 'answers' : 'תשובות'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {q.status !== 'resolved' && (
                  <>
                    <Button
                      size="sm"
                      variant="primary"
                      title={language === 'en' ? 'Add prediction for user' : 'הוסף תחזית למשתמש'}
                      onClick={() => openAnswerModal(q._id)}
                    >
                      <UserPlus className="h-4 w-4" />
                      {language === 'en' ? 'Add prediction' : 'הוסף תחזית'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => openEdit(q)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="success" onClick={() => openResolve(q)}>
                      <CheckCircle className="h-4 w-4" />
                      {language === 'en' ? 'Resolve' : 'פתור'}
                    </Button>
                  </>
                )}
                <Button size="sm" variant="danger" onClick={() => handleDelete(q)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {(q.answers || []).length > 0 && (
              <ul className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-slate-100 text-sm">
                {q.answers.map((a) => (
                  <li key={a._id} className="flex justify-between border-b border-slate-50 px-2 py-1">
                    <span>{a.userId?.username}</span>
                    <span>{formatAnswerLabel(a, q, language, getTeamDisplayName)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
        {questions.length === 0 && !loading && (
          <p className="text-center text-slate-500">
            {language === 'en' ? 'No questions yet.' : 'אין שאלות עדיין.'}
          </p>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-xl font-bold">
              {editingId
                ? language === 'en'
                  ? 'Edit question'
                  : 'ערוך שאלה'
                : language === 'en'
                  ? 'New question'
                  : 'שאלה חדשה'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                className="w-full rounded-lg border px-3 py-2 text-sm"
                placeholder={language === 'en' ? 'Title (Hebrew)' : 'כותרת בעברית'}
                value={form.titleHe}
                onChange={(e) => setForm({ ...form, titleHe: e.target.value })}
                required
                dir="rtl"
              />
              <input
                className="w-full rounded-lg border px-3 py-2 text-sm"
                placeholder={language === 'en' ? 'Title (English)' : 'כותרת באנגלית'}
                value={form.titleEn}
                onChange={(e) => setForm({ ...form, titleEn: e.target.value })}
                required
              />
              <select
                className="w-full rounded-lg border px-3 py-2 text-sm"
                value={form.type}
                onChange={(e) => {
                  const type = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    type,
                    options:
                      type === 'options' && prev.options.length < 2
                        ? defaultOptions()
                        : prev.options
                  }));
                }}
              >
                <option value="team">{language === 'en' ? 'Pick a team' : 'בחירת נבחרת'}</option>
                <option value="text">{language === 'en' ? 'Free text' : 'טקסט חופשי'}</option>
                <option value="options">{language === 'en' ? 'Fixed options' : 'אפשרויות קבועות'}</option>
              </select>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                placeholder={language === 'en' ? 'Points if correct' : 'נקודות אם נכון'}
                value={form.points}
                onChange={(e) => setForm({ ...form, points: e.target.value })}
              />
              {form.type === 'options' && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    {language === 'en'
                      ? 'Answer options (minimum 2)'
                      : 'אפשרויות תשובה (מינימום 2)'}
                  </p>
                  {form.options.map((opt, i) => (
                    <div key={`${opt.optionId}-${i}`} className="flex gap-2">
                      <div className="grid flex-1 gap-2 sm:grid-cols-2">
                        <input
                          className="rounded-lg border px-2 py-1 text-sm"
                          placeholder={`${language === 'en' ? 'Option' : 'אפשרות'} ${i + 1} HE`}
                          value={opt.labelHe}
                          onChange={(e) => {
                            const opts = [...form.options];
                            opts[i] = { ...opts[i], labelHe: e.target.value };
                            setForm({ ...form, options: opts });
                          }}
                          dir="rtl"
                        />
                        <input
                          className="rounded-lg border px-2 py-1 text-sm"
                          placeholder={`${language === 'en' ? 'Option' : 'אפשרות'} ${i + 1} EN`}
                          value={opt.labelEn}
                          onChange={(e) => {
                            const opts = [...form.options];
                            opts[i] = { ...opts[i], labelEn: e.target.value };
                            setForm({ ...form, options: opts });
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="shrink-0 px-2"
                        disabled={form.options.length <= 2}
                        onClick={() => removeOption(i)}
                        aria-label={language === 'en' ? 'Remove option' : 'הסר אפשרות'}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="secondary" onClick={addOption}>
                    <Plus className="h-4 w-4" />
                    {language === 'en' ? 'Add option' : 'הוסף אפשרות'}
                  </Button>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('save')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAnswerModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {language === 'en' ? 'Add prediction for user' : 'הוסף תחזית למשתמש'}
              </h2>
              <button
                type="button"
                onClick={() => setShowAnswerModal(false)}
                className="rounded-lg p-1 text-zinc-500 hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAnswerSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-800">
                  {language === 'en' ? 'User' : 'משתמש'} *
                </label>
                <input
                  type="text"
                  className="mb-2 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                  placeholder={language === 'en' ? 'Search users…' : 'חפש משתמשים…'}
                  value={searchUserTerm}
                  onChange={(e) => setSearchUserTerm(e.target.value)}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                <select
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                  value={answerForm.userId}
                  onChange={(e) => setAnswerForm({ ...answerForm, userId: e.target.value })}
                  required
                >
                  <option value="">{language === 'en' ? 'Select user' : 'בחר משתמש'}</option>
                  {filteredUsers.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.fullName || user.username} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-800">
                  {language === 'en' ? 'Question' : 'שאלה'} *
                </label>
                <select
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                  value={answerForm.questionId}
                  onChange={(e) =>
                    setAnswerForm({
                      ...answerForm,
                      questionId: e.target.value,
                      teamGroupId: '',
                      text: '',
                      optionId: ''
                    })
                  }
                  required
                >
                  <option value="">{language === 'en' ? 'Select question' : 'בחר שאלה'}</option>
                  {editableQuestions.map((q) => (
                    <option key={q._id} value={q._id}>
                      {getQuestionTitle(q, language)}
                      {!q.isOpen ? (language === 'en' ? ' (locked for users)' : ' (נעול למשתמשים)') : ''}
                    </option>
                  ))}
                </select>
              </div>
              {selectedAnswerQuestion?.type === 'team' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-zinc-800">
                    {language === 'en' ? 'Team' : 'נבחרת'} *
                  </label>
                  <select
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                    value={answerForm.teamGroupId}
                    onChange={(e) => setAnswerForm({ ...answerForm, teamGroupId: e.target.value })}
                    required
                  >
                    <option value="">{language === 'en' ? 'Select team' : 'בחר נבחרת'}</option>
                    {teams.map((team) => (
                      <option key={team._id} value={team._id}>
                        {getTeamDisplayName(team, language)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedAnswerQuestion?.type === 'text' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-zinc-800">
                    {language === 'en' ? 'Answer' : 'תשובה'} *
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                    value={answerForm.text}
                    onChange={(e) => setAnswerForm({ ...answerForm, text: e.target.value })}
                    required
                  />
                </div>
              )}
              {selectedAnswerQuestion?.type === 'options' &&
                selectedAnswerQuestion.options?.map((opt) => (
                  <label key={opt.optionId} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="admin-answer-opt"
                      value={opt.optionId}
                      checked={answerForm.optionId === opt.optionId}
                      onChange={() => setAnswerForm({ ...answerForm, optionId: opt.optionId })}
                    />
                    {getOptionLabel(opt, language)}
                  </label>
                ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setShowAnswerModal(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" loading={savingAnswer}>
                  {language === 'en' ? 'Save prediction' : 'שמור תחזית'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resolveQ && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold">
              {language === 'en' ? 'Set correct answer' : 'הגדר תשובה נכונה'}
            </h2>
            <p className="mb-3 text-sm text-slate-600">{getQuestionTitle(resolveQ, language)}</p>
            {resolveQ.type === 'team' && (
              <select
                className="w-full rounded-lg border px-3 py-2"
                value={resolveValue.teamGroupId || ''}
                onChange={(e) => setResolveValue({ teamGroupId: e.target.value })}
              >
                <option value="">{language === 'en' ? 'Select winner' : 'בחר מנצחת'}</option>
                {teams.map((team) => (
                  <option key={team._id} value={team._id}>
                    {getTeamDisplayName(team, language)}
                  </option>
                ))}
              </select>
            )}
            {resolveQ.type === 'text' && (
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    {language === 'en'
                      ? 'Select every answer that should count as correct'
                      : 'בחר את כל התשובות שייחשבו נכונות'}
                  </p>
                  <div className="max-h-52 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-2">
                    {textAnswerChoices.map((choice) => {
                      const selected = (resolveValue.acceptedTexts || []).some(
                        (value) => value.toLowerCase() === choice.text.toLowerCase()
                      );
                      return (
                        <button
                          key={choice.text.toLowerCase()}
                          type="button"
                          onClick={() => toggleAcceptedText(choice.text)}
                          className={[
                            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-start text-sm transition-colors',
                            selected
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          ].join(' ')}
                        >
                          <span>{choice.text}</span>
                          <span className="text-xs font-semibold">×{choice.count}</span>
                        </button>
                      );
                    })}
                    {textAnswerChoices.length === 0 && (
                      <p className="px-2 py-3 text-center text-sm text-slate-500">
                        {language === 'en' ? 'No user answers yet' : 'אין עדיין תשובות משתמשים'}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    {language === 'en' ? 'Add another accepted spelling' : 'הוסף איות נכון נוסף'}
                  </p>
                  <div className="flex gap-2">
                    <input
                      className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                      value={resolveValue.customText || ''}
                      onChange={(e) =>
                        setResolveValue((current) => ({
                          ...current,
                          customText: e.target.value
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomAcceptedText();
                        }
                      }}
                    />
                    <Button type="button" variant="secondary" onClick={addCustomAcceptedText}>
                      <Plus className="h-4 w-4" />
                      {language === 'en' ? 'Add' : 'הוסף'}
                    </Button>
                  </div>
                </div>

                {(resolveValue.acceptedTexts || []).length > 0 && (
                  <div className="rounded-xl bg-emerald-50 p-3">
                    <p className="mb-2 text-sm font-semibold text-emerald-900">
                      {language === 'en' ? 'Accepted answers' : 'תשובות נכונות'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {resolveValue.acceptedTexts.map((text) => (
                        <button
                          key={text.toLowerCase()}
                          type="button"
                          onClick={() => toggleAcceptedText(text)}
                          className="rounded-full bg-white px-3 py-1 text-xs text-emerald-900 shadow-sm"
                          title={language === 'en' ? 'Click to remove' : 'לחץ להסרה'}
                        >
                          {text} ×
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-emerald-800">
                      {language === 'en' ? 'Will receive points: ' : 'יקבלו נקודות: '}
                      {textAnswerChoices
                        .filter((choice) =>
                          (resolveValue.acceptedTexts || []).some(
                            (value) => value.toLowerCase() === choice.text.toLowerCase()
                          )
                        )
                        .flatMap((choice) => choice.users)
                        .filter(Boolean)
                        .join(', ') || (language === 'en' ? 'No existing users' : 'אין משתמשים קיימים')}
                    </p>
                  </div>
                )}
              </div>
            )}
            {resolveQ.type === 'options' &&
              resolveQ.options?.map((opt) => (
                <label key={opt.optionId} className="mb-2 flex items-center gap-2">
                  <input
                    type="radio"
                    name="resolve-opt"
                    value={opt.optionId}
                    onChange={() => setResolveValue({ optionId: opt.optionId })}
                  />
                  {getOptionLabel(opt, language)}
                </label>
              ))}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setResolveQ(null)}>
                {t('cancel')}
              </Button>
              <Button onClick={submitResolve}>{language === 'en' ? 'Resolve' : 'פתור'}</Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminQuestions;
